import React, { useState, useRef } from 'react';
import '../Contact.css';
import { useLang } from '../context/LanguageContext';
import en from '../lang/en';
import de from '../lang/de';
import it from '../lang/it';
import fr from '../lang/fr';
import { kontackVideo } from '../assets/media';

function ContactForm({ t, status, form, formRef, handleChange, handleSubmit, fieldErrors = {} }) {
  return (
    <div className="contact-form-wrap">
      {status === 'success' ? (
        <div className="contact-success form-anim">
          <span className="success-icon">✓</span>
          <h3>{t.successHeading}</h3>
          <p>{t.successBody}</p>
        </div>
      ) : (
        <form ref={formRef} className="contact-form" onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className="form-group form-anim" style={{ animationDelay: '0.2s' }}>
              <label>{t.form.nameLabel}</label>
              <input type="text" name="name" value={form.name} onChange={handleChange} placeholder={t.form.namePlaceholder}
                style={fieldErrors.name ? { borderColor: '#fc0071' } : {}} />
              {fieldErrors.name && <span style={{ fontSize: 12, color: '#fc0071', marginTop: 4, display: 'block' }}>{fieldErrors.name}</span>}
            </div>
            <div className="form-group form-anim" style={{ animationDelay: '0.3s' }}>
              <label>{t.form.emailLabel}</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} placeholder={t.form.emailPlaceholder}
                style={fieldErrors.email ? { borderColor: '#fc0071' } : {}} />
              {fieldErrors.email && <span style={{ fontSize: 12, color: '#fc0071', marginTop: 4, display: 'block' }}>{fieldErrors.email}</span>}
            </div>
          </div>
          <div className="form-group form-anim" style={{ animationDelay: '0.4s' }}>
            <label>{t.form.companyLabel}</label>
            <input type="text" name="company" value={form.company} onChange={handleChange} placeholder={t.form.companyPlaceholder} />
          </div>
          <div className="form-group form-anim" style={{ animationDelay: '0.5s' }}>
            <label>{t.form.messageLabel}</label>
            <textarea name="message" value={form.message} onChange={handleChange} placeholder={t.form.messagePlaceholder} rows={5}
              style={fieldErrors.message ? { borderColor: '#fc0071' } : {}} />
            {fieldErrors.message && <span style={{ fontSize: 12, color: '#fc0071', marginTop: 4, display: 'block' }}>{fieldErrors.message}</span>}
          </div>
          {status === 'error' && <p className="form-error">{t.errorMsg}</p>}
          <button type="submit" className="contact-submit form-anim" style={{ animationDelay: '0.6s' }} disabled={status === 'sending'}>
            {status === 'sending' ? t.form.sending : <>{t.form.submit} <span className="submit-arrow">→</span></>}
          </button>
        </form>
      )}
    </div>
  );
}

function Contact() {
  const { lang } = useLang();
  const t = lang === 'de' ? de.contact : lang === 'it' ? it.contact : lang === 'fr' ? fr.contact : en.contact;
  const imp = t.impression;

  const [current, setCurrent] = useState(0);
  const formRef = useRef(null);
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (fieldErrors[e.target.name]) setFieldErrors(fe => ({ ...fe, [e.target.name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim())    errs.name    = 'Name is required';
    if (!form.email.trim())   errs.email   = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = 'Enter a valid email address';
    if (!form.message.trim()) errs.message = 'Message is required';
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }

    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', company: '', message: '' });
        setFieldErrors({});
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="contact-page">
      <video ref={el => { if (el) el.playbackRate = 0.5; }} className="page-bg-video" src={kontackVideo} autoPlay loop muted playsInline />
      <div className="page-bg-overlay"></div>

      {/* Slide 0 — Contact */}
      {current === 0 && (
        <div className="contact-layout contact-slide-in" key="contact">
          <div className="contact-info">
            <span className="contact-tag info-anim" style={{ animationDelay: '0.1s' }}>{t.tag}</span>
            <h2 className="contact-heading info-anim" style={{ animationDelay: '0.2s' }}>
              {t.heading}
            </h2>
            <p className="contact-intro info-anim" style={{ animationDelay: '0.3s' }}>{t.intro}</p>
            <div className="contact-details">
              <div className="contact-detail-item info-anim" style={{ animationDelay: '0.4s' }}>
                <span className="detail-icon">◎</span>
                <div>
                  <span className="detail-label">{t.addressLabel}</span>
                  <span className="detail-value">7Elemente IT-Solutions UG</span>
                  <span className="detail-value">Bahnhofstraße 11, 38642 Goslar</span>
                </div>
              </div>
              <div className="contact-detail-item info-anim" style={{ animationDelay: '0.5s' }}>
                <span className="detail-icon">✉</span>
                <div>
                  <span className="detail-label">{t.emailLabel}</span>
                  <a className="detail-value detail-link" href="mailto:info@7Elemente.com">info@7Elemente.com</a>
                </div>
              </div>
              <div className="contact-detail-item info-anim" style={{ animationDelay: '0.6s' }}>
                <span className="detail-icon">✆</span>
                <div>
                  <span className="detail-label">{t.phoneLabel}</span>
                  <a className="detail-value detail-link detail-phone" href="tel:+4953213949496">+49 5321 39 49 49 6</a>
                </div>
              </div>
            </div>
          </div>
          <ContactForm t={t} status={status} form={form} formRef={formRef} handleChange={handleChange} handleSubmit={handleSubmit} fieldErrors={fieldErrors} />
        </div>
      )}

      {/* Slide 1 — Impression */}
      {current === 1 && (
        <div className="contact-layout contact-slide-in" key="impression">
          <div className="contact-info">
            <span className="contact-tag info-anim" style={{ animationDelay: '0.1s' }}>{imp.tag}</span>
            <h2 className="contact-heading info-anim" style={{ animationDelay: '0.2s' }}>
              {imp.titleBefore}<span className="contact-highlight">{imp.titleHighlight}</span>
            </h2>
            <p className="contact-intro info-anim" style={{ animationDelay: '0.3s' }}>{imp.subtitle}</p>
            <div className="contact-details">
              <div className="contact-detail-item info-anim" style={{ animationDelay: '0.4s' }}>
                <span className="detail-icon">◎</span>
                <div>
                  <span className="detail-label">{t.addressLabel}</span>
                  <span className="detail-value">7Elemente IT-Solutions UG</span>
                  <span className="detail-value">Bahnhofstraße 11, 38642 Goslar</span>
                </div>
              </div>
              <div className="contact-detail-item info-anim" style={{ animationDelay: '0.5s' }}>
                <span className="detail-icon">✉</span>
                <div>
                  <span className="detail-label">{t.emailLabel}</span>
                  <a className="detail-value detail-link" href="mailto:info@7Elemente.com">info@7Elemente.com</a>
                </div>
              </div>
              <div className="contact-detail-item info-anim" style={{ animationDelay: '0.6s' }}>
                <span className="detail-icon">✆</span>
                <div>
                  <span className="detail-label">{t.phoneLabel}</span>
                  <a className="detail-value detail-link detail-phone" href="tel:+4953213949496">+49 5321 39 49 49 6</a>
                </div>
              </div>
              <div className="contact-detail-item info-anim" style={{ animationDelay: '0.7s' }}>
                <span className="detail-icon">◈</span>
                <div>
                  <span className="detail-label">{lang === 'de' ? 'Registergericht' : lang === 'it' ? 'Registro' : lang === 'fr' ? 'Registre' : 'Register'}</span>
                  <span className="detail-value">Amtsgericht Braunschweig</span>
                </div>
              </div>
            </div>
          </div>
          <ContactForm t={t} status={status} form={form} formRef={formRef} handleChange={handleChange} handleSubmit={handleSubmit} fieldErrors={fieldErrors} />
        </div>
      )}

      {/* Navigation */}
      <div className="contact-nav">
        <button className={`contact-nav-label${current === 0 ? ' active' : ''}`} onClick={() => setCurrent(0)}>
          {t.navContact}
        </button>
        <button className={`contact-nav-label${current === 1 ? ' active' : ''}`} onClick={() => setCurrent(1)}>
          {t.navImpression}
        </button>
      </div>
    </div>
  );
}

export default Contact;
